import { ApolloLink, HttpLink, ApolloClient, split, Observable } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient, type Client } from 'graphql-ws';
import * as Sentry from '@sentry/react';

import config from '@occc/config';

import AuthMiddleware from './AuthMiddleware';
import { sentryMiddleware } from './sentryMiddleware';
import { cache } from './apollo-client-cache';

import type { AuthService } from '../Auth/authService';

const httpLink = new HttpLink({
  uri: config.api.commandUrl,
});

// Lazy WebSocket client with disposal support
let wsClient: Client | null = null;
let wsLink: GraphQLWsLink | null = null;

// Lazy reference to authService to break circular dependency
let authServiceRef: AuthService | null = null;

const createWsClient = (url: string): Client => {
  return createClient({
    url,
    // Send a keep-alive ping every 30s to prevent intermediate proxies
    // (ALB, nginx, etc.) from closing idle WebSocket connections.
    keepAlive: 30_000,
    connectionParams: () => {
      const token = authServiceRef?.getAccessToken();
      const params = {
        'x-api-key': config.api.commandKey,
        authorization: token ? `Bearer ${token}` : undefined,
      };
      return params;
    },
    retryAttempts: 5,
    shouldRetry: () => true,
    on: {
      connecting: () => {
        // Connection lifecycle events - only log errors to Sentry
      },
      connected: () => {
        // Connection lifecycle events - only log errors to Sentry
      },
      closed: () => {
        // Connection lifecycle events - only log errors to Sentry
      },
      error: (error) => {
        Sentry.captureException(error, {
          tags: {
            component: 'ApolloClient',
            action: 'websocketError',
          },
        });
      },
    },
  });
};

const getWsClient = (url: string): Client => {
  if (!wsClient) {
    wsClient = createWsClient(url);
  }
  return wsClient;
};

const getWsLink = (url: string): GraphQLWsLink => {
  if (!wsLink) {
    wsLink = new GraphQLWsLink(getWsClient(url));
  }
  return wsLink;
};

export const disposeWsClient = (): void => {
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
    wsLink = null;
  }
};

const createLazyWsLink = (url: string) =>
  new ApolloLink((operation) => {
    const link = getWsLink(url);
    return link.request(operation) || Observable.of();
  });

const authMiddleware = new AuthMiddleware();

const xApiKeyMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-api-key': config.api.commandKey,
    },
  }));

  return forward(operation);
});

const httpChain = ApolloLink.from([xApiKeyMiddleware, authMiddleware, sentryMiddleware, httpLink]);

// When WS URL is configured, subscriptions go over WebSocket.
// When absent, subscriptions get an explicit error so misconfiguration
// surfaces clearly rather than failing silently over HTTP.
const subscriptionLink = config.api.commandWsUrl
  ? createLazyWsLink(config.api.commandWsUrl)
  : new ApolloLink(() => {
      return new Observable((observer) => {
        const error = new Error(
          'GraphQL subscriptions are not configured: REACT_APP_COMMAND_API_WS_URL is missing',
        );

        Sentry.captureException(error, {
          tags: {
            component: 'ApolloClient',
            action: 'missingWebSocketUrl',
          },
        });

        observer.error(error);
      });
    });

const transportLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  subscriptionLink,
  httpChain,
);

export const apolloClient = new ApolloClient({
  link: transportLink,
  cache,
});

export const initializeAuth = (authService: AuthService): void => {
  authServiceRef = authService;
  authMiddleware.injectAuthService(authService);
  authService.injectNetworkClient(apolloClient);
  authService.injectDisposeWsClient(disposeWsClient);
};
